import { useEffect, useState, useMemo, type CSSProperties } from "react";
import {
  getCharitySummary,
  getCharityFunds,
  listCharityItems,
  searchCatalogProducts,
  upsertCharityItem,
  type CharitySummaryStats,
  type CharityItem,
  type CharityEventFund,
} from "../../lib/api";
import PublishingStudioTabs from "../../components/admin/PublishingStudioTabs";

export default function AdminCharity() {
  const [tab, setTab] = useState<"items" | "events">("items");

  // Summary Stats
  const [stats, setStats] = useState<CharitySummaryStats | null>(null);

  // Charity Items
  const [charityItems, setCharityItems] = useState<CharityItem[]>([]);
  const [itemsSearch, setItemsSearch] = useState("");
  const [loadingItems, setLoadingItems] = useState(true);

  // Charity Events
  const [charityEvents, setCharityEvents] = useState<CharityEventFund[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Modal State for Add / Edit
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");
  const [selectedProduct, setSelectedProduct] = useState<CharityItem | null>(null);
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState<CharityItem[]>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [donationDollars, setDonationDollars] = useState("");
  const [donationDesc, setDonationDesc] = useState("");
  const [savingItem, setSavingItem] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  // Messages
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 4000);
  };

  const loadStatsData = async () => {
    try {
      const res = await getCharitySummary();
      setStats(res);
    } catch {}
  };

  const loadItemsData = async () => {
    setLoadingItems(true);
    setErrorMsg(null);
    try {
      const items = await listCharityItems();
      setCharityItems(items);
    } catch (err: any) {
      setErrorMsg("Failed to load charity items: " + (err?.message || "Error"));
    } finally {
      setLoadingItems(false);
    }
  };

  const loadEventsData = async () => {
    setLoadingEvents(true);
    try {
      const funds = await getCharityFunds();
      setCharityEvents(funds);
    } catch (err: any) {
      setErrorMsg("Failed to load charity events: " + (err?.message || "Error"));
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    loadStatsData();
    loadItemsData();
  }, []);

  useEffect(() => {
    if (tab === "events" && charityEvents.length === 0) {
      loadEventsData();
    }
  }, [tab]);

  // Product Autocomplete for Modal
  useEffect(() => {
    if (!productSearch.trim() || modalMode === "edit") {
      setProductResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchingProducts(true);
      try {
        const results = await searchCatalogProducts(productSearch.trim());
        setProductResults(results);
      } catch {
        setProductResults([]);
      } finally {
        setSearchingProducts(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [productSearch, modalMode]);

  // Modal Handlers
  const handleOpenAdd = () => {
    setModalMode("add");
    setSelectedProduct(null);
    setProductSearch("");
    setProductResults([]);
    setDonationDollars("");
    setDonationDesc("");
    setModalError(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (item: CharityItem) => {
    setModalMode("edit");
    setSelectedProduct(item);
    setProductSearch("");
    setProductResults([]);
    setDonationDollars(
      item.charity_donation_cents ? (item.charity_donation_cents / 100).toFixed(2) : ""
    );
    setDonationDesc(item.charity_description || "");
    setModalError(null);
    setModalOpen(true);
  };

  const handleSaveModal = async () => {
    if (!selectedProduct) {
      setModalError("Please select a catalog product first.");
      return;
    }
    const amountCents = Math.round(parseFloat(donationDollars || "0") * 100);
    setSavingItem(true);
    setModalError(null);
    try {
      await upsertCharityItem({
        id: selectedProduct.id,
        is_charity_item: true,
        charity_donation_cents: amountCents,
        charity_description: donationDesc.trim() || null,
      });
      setModalOpen(false);
      showToast(`Updated charity item "${selectedProduct.name}".`);
      await loadItemsData();
      await loadStatsData();
    } catch (err: any) {
      setModalError("Save failed: " + (err?.message || "Error"));
    } finally {
      setSavingItem(false);
    }
  };

  const handleRemoveCharity = async (item: CharityItem) => {
    if (
      !window.confirm(
        `Remove "${item.name}" from charity items? This will remove the S3 Gives Back flag and per-pour donation tracking.`
      )
    ) {
      return;
    }
    try {
      await upsertCharityItem({
        id: item.id,
        is_charity_item: false,
        charity_donation_cents: 0,
        charity_description: null,
      });
      showToast(`Removed "${item.name}" from charity items.`);
      await loadItemsData();
      await loadStatsData();
    } catch (err: any) {
      alert("Failed to remove charity item: " + (err?.message || "Error"));
    }
  };

  const filteredItems = useMemo(() => {
    if (!itemsSearch.trim()) return charityItems;
    const q = itemsSearch.toLowerCase();
    return charityItems.filter((i) => i.name.toLowerCase().includes(q));
  }, [charityItems, itemsSearch]);

  const fmtCurrency = (cents?: number | null) =>
    "$" + (Math.round((cents || 0) / 100)).toLocaleString("en-US");

  return (
    <div style={styles.container}>
      <PublishingStudioTabs activeTab="charity" badgeCounts={{ charity: charityItems.length }} />
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>S3 Giving & Charity Ledger</h1>
          <p style={styles.subtitle}>
            Sentinel Society Service (S3) charitable contributions, bottle donations, and event funds
          </p>
        </div>
        <div style={styles.tabToggle}>
          <button
            style={{
              ...styles.tabBtn,
              ...(tab === "items" ? styles.tabBtnActive : {}),
            }}
            onClick={() => setTab("items")}
          >
            Charity Items ({charityItems.length})
          </button>
          <button
            style={{
              ...styles.tabBtn,
              ...(tab === "events" ? styles.tabBtnActive : {}),
            }}
            onClick={() => setTab("events")}
          >
            Charity Events ({charityEvents.length})
          </button>
        </div>
      </div>

      {/* Messages */}
      {errorMsg && <div style={styles.errorBanner}>{errorMsg}</div>}
      {successMsg && <div style={styles.successBanner}>{successMsg}</div>}

      {/* Summary KPI Cards */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{fmtCurrency(stats?.this_quarter_cents)}</div>
          <div style={styles.statLabel}>This Quarter</div>
          <div style={styles.statSub}>S3 Community Pours</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{fmtCurrency(stats?.this_year_cents)}</div>
          <div style={styles.statLabel}>This Year</div>
          <div style={styles.statSub}>Year to date contributions</div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statVal, color: "var(--gold, #F9AF36)" }}>
            {fmtCurrency(stats?.all_time_cents)}
          </div>
          <div style={styles.statLabel}>All Time Given</div>
          <div style={styles.statSub}>Total community impact</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statVal}>{stats?.all_time_tickets || 0}</div>
          <div style={styles.statLabel}>Tickets Given Back</div>
          <div style={styles.statSub}>Charity ticket units</div>
        </div>
      </div>

      {/* ═══ TAB 1: CHARITY ITEMS ═══ */}
      {tab === "items" && (
        <div>
          <div style={styles.toolbar}>
            <input
              type="text"
              style={styles.searchInput}
              placeholder="Search charity bottles and menu items..."
              value={itemsSearch}
              onChange={(e) => setItemsSearch(e.target.value)}
            />
            <button style={styles.addBtn} onClick={handleOpenAdd}>
              + Add Charity Item
            </button>
          </div>

          <div style={styles.itemsList}>
            {loadingItems ? (
              <div style={styles.empty}>Loading charity items...</div>
            ) : filteredItems.length === 0 ? (
              <div style={styles.empty}>
                {itemsSearch ? "No matching charity items." : "No charity items configured yet."}
              </div>
            ) : (
              filteredItems.map((item) => {
                const donation = item.charity_donation_cents
                  ? `$${(item.charity_donation_cents / 100).toFixed(2)}/unit`
                  : "No amount set";

                return (
                  <div
                    key={item.id}
                    style={{
                      ...styles.itemCard,
                      ...(item.is_active === false ? { opacity: 0.6 } : {}),
                    }}
                  >
                    <div style={styles.itemInfo}>
                      <div style={styles.itemName}>
                        {item.name}
                        {item.is_active === false && (
                          <span style={styles.inactiveTag}> (Inactive)</span>
                        )}
                      </div>
                      <div style={styles.itemMeta}>
                        {[item.category_slug, item.item_type, item.charity_description]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    <div style={styles.itemDonation}>{donation}</div>
                    <div style={styles.itemActions}>
                      <button style={styles.editBtn} onClick={() => handleOpenEdit(item)}>
                        Edit
                      </button>
                      <button
                        style={styles.delBtn}
                        onClick={() => handleRemoveCharity(item)}
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ═══ TAB 2: CHARITY EVENTS ═══ */}
      {tab === "events" && (
        <div style={styles.tableCard}>
          <div style={styles.tableHeadRow}>
            <div style={styles.sectionHeading}>
              Charity Fundraising Events ({charityEvents.length})
            </div>
            <button style={styles.refreshBtn} onClick={loadEventsData}>
              ↻ Refresh Events
            </button>
          </div>

          {loadingEvents ? (
            <div style={styles.empty}>Loading charity events...</div>
          ) : charityEvents.length === 0 ? (
            <div style={styles.empty}>No charity events recorded.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Event Title</th>
                  <th style={styles.th}>Period</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Attendees</th>
                  <th style={{ ...styles.th, textAlign: "right" }}>Total Raised</th>
                </tr>
              </thead>
              <tbody>
                {charityEvents.map((evt, idx) => (
                  <tr key={evt.id || idx} style={styles.tr}>
                    <td style={styles.td}>
                      <strong>{evt.title}</strong>
                    </td>
                    <td style={styles.td}>
                      Q{evt.quarter || "—"} {evt.year || ""}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right" }}>
                      {evt.total_headcount || 0}
                    </td>
                    <td style={{ ...styles.td, textAlign: "right", color: "#4ade80", fontWeight: 700 }}>
                      {fmtCurrency(evt.total_raised_cents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ═══ ADD / EDIT MODAL ═══ */}
      {modalOpen && (
        <div style={styles.modalOverlay} onClick={() => setModalOpen(false)}>
          <div style={styles.modalBody} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {modalMode === "add" ? "Add Charity Item" : "Edit Charity Item"}
              </h2>
              <button style={styles.closeBtn} onClick={() => setModalOpen(false)}>
                ✕
              </button>
            </div>

            {/* Product Selector */}
            {modalMode === "add" && !selectedProduct && (
              <div style={styles.modalField}>
                <label style={styles.fieldLabel}>Search Catalog Product</label>
                <input
                  type="text"
                  style={styles.modalInput}
                  placeholder="Type to search bottles, pours, retail..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
                {searchingProducts && (
                  <div style={styles.searchingStatus}>Searching catalog...</div>
                )}
                {productResults.length > 0 && (
                  <div style={styles.dropdown}>
                    {productResults.map((p) => (
                      <div
                        key={p.id}
                        style={styles.dropdownItem}
                        onClick={() => setSelectedProduct(p)}
                      >
                        <div style={styles.dropdownName}>{p.name}</div>
                        <div style={styles.dropdownSub}>
                          {[p.category_slug, p.item_type].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Selected Product Card */}
            {selectedProduct && (
              <div style={styles.selectedBox}>
                <div style={styles.selectedName}>{selectedProduct.name}</div>
                <div style={styles.selectedMeta}>
                  {[selectedProduct.category_slug, selectedProduct.item_type]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
                {modalMode === "add" && (
                  <button
                    style={styles.changeLink}
                    onClick={() => setSelectedProduct(null)}
                  >
                    Change Product
                  </button>
                )}
              </div>
            )}

            {/* Donation Amount Per Unit */}
            <div style={styles.modalField}>
              <label style={styles.fieldLabel}>Donation Amount Per Unit ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                style={styles.modalInput}
                placeholder="25.00"
                value={donationDollars}
                onChange={(e) => setDonationDollars(e.target.value)}
              />
            </div>

            {/* Description */}
            <div style={styles.modalField}>
              <label style={styles.fieldLabel}>Charity Description (optional)</label>
              <input
                type="text"
                style={styles.modalInput}
                placeholder="e.g. $25 per bottle to local veteran charities"
                value={donationDesc}
                onChange={(e) => setDonationDesc(e.target.value)}
              />
            </div>

            {modalError && <div style={styles.modalError}>{modalError}</div>}

            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button
                style={styles.saveBtn}
                disabled={savingItem}
                onClick={handleSaveModal}
              >
                {savingItem ? "Saving..." : "Save Charity Item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px 0",
  },
  subtitle: {
    fontSize: 13,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  tabToggle: {
    display: "flex",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: 3,
  },
  tabBtn: {
    background: "transparent",
    border: "none",
    color: "var(--muted, #8A7A66)",
    padding: "6px 14px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  tabBtnActive: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
  },
  errorBanner: {
    background: "rgba(224, 80, 64, 0.15)",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  successBanner: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#4ade80",
    padding: "10px 14px",
    borderRadius: 6,
    marginBottom: 16,
    fontSize: 13,
  },
  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 12,
    marginBottom: 24,
  },
  statCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    padding: "16px",
    textAlign: "center",
  },
  statVal: {
    fontFamily: "Georgia, serif",
    fontSize: 26,
    fontWeight: 700,
    color: "#4ade80",
  },
  statLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    color: "var(--muted, #8A7A66)",
    marginTop: 4,
  },
  statSub: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  toolbar: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    marginBottom: 16,
  },
  searchInput: {
    flex: 1,
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 5,
    padding: "8px 12px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
  },
  addBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 5,
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  itemsList: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  itemCard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
  },
  itemInfo: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--text, #F0E6D4)",
  },
  inactiveTag: {
    color: "#ff8b7b",
    fontSize: 11,
    fontWeight: 400,
  },
  itemMeta: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  itemDonation: {
    fontSize: 14,
    fontWeight: 700,
    color: "#4ade80",
    whiteSpace: "nowrap",
    marginLeft: 16,
  },
  itemActions: {
    display: "flex",
    gap: 6,
    marginLeft: 16,
  },
  editBtn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 4,
    padding: "5px 12px",
    fontSize: 12,
    cursor: "pointer",
  },
  delBtn: {
    background: "transparent",
    border: "1px solid rgba(224, 80, 64, 0.4)",
    color: "#ff8b7b",
    borderRadius: 4,
    padding: "5px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
  tableCard: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 8,
    // auto, not hidden: on phones the columns past the edge were clipped and unreachable.
    overflowX: "auto",
  },
  tableHeadRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: "1px solid var(--border, #2A1F14)",
  },
  sectionHeading: {
    fontSize: 13,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  refreshBtn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--text, #F0E6D4)",
    borderRadius: 4,
    padding: "4px 10px",
    fontSize: 11,
    cursor: "pointer",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
  },
  th: {
    background: "rgba(0,0,0,0.3)",
    textAlign: "left",
    padding: "10px 14px",
    color: "var(--gold, #F9AF36)",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid var(--border, #2A1F14)",
  },
  tr: {
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  td: {
    padding: "10px 14px",
    color: "var(--text, #F0E6D4)",
  },
  empty: {
    textAlign: "center",
    padding: 40,
    color: "var(--muted, #8A7A66)",
  },

  // Modal
  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
  },
  modalBody: {
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 10,
    width: 480,
    maxWidth: "95vw",
    padding: 24,
    position: "relative",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    borderBottom: "1px solid var(--border, #2A1F14)",
    paddingBottom: 10,
  },
  modalTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    color: "var(--gold, #F9AF36)",
    margin: 0,
  },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 18,
    color: "var(--muted, #8A7A66)",
    cursor: "pointer",
  },
  modalField: {
    marginBottom: 14,
    position: "relative",
  },
  fieldLabel: {
    display: "block",
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.15em",
    color: "var(--muted, #8A7A66)",
    marginBottom: 4,
  },
  modalInput: {
    width: "100%",
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 4,
    padding: "8px 12px",
    fontSize: 13,
    color: "var(--text, #F0E6D4)",
    boxSizing: "border-box",
  },
  searchingStatus: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 4,
    fontStyle: "italic",
  },
  dropdown: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    background: "var(--surface, #13100B)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 4,
    maxHeight: 180,
    overflowY: "auto",
    zIndex: 20,
    marginTop: 2,
  },
  dropdownItem: {
    padding: "8px 12px",
    cursor: "pointer",
    borderBottom: "1px solid rgba(255,255,255,0.03)",
  },
  dropdownName: {
    fontSize: 13,
    fontWeight: 600,
    color: "var(--text, #F0E6D4)",
  },
  dropdownSub: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  selectedBox: {
    background: "rgba(0,0,0,0.25)",
    border: "1px solid var(--border, #2A1F14)",
    borderRadius: 6,
    padding: "10px 12px",
    marginBottom: 14,
  },
  selectedName: {
    fontSize: 14,
    fontWeight: 700,
    color: "var(--gold, #F9AF36)",
  },
  selectedMeta: {
    fontSize: 12,
    color: "var(--muted, #8A7A66)",
    marginTop: 2,
  },
  changeLink: {
    background: "none",
    border: "none",
    color: "#ff8b7b",
    fontSize: 11,
    cursor: "pointer",
    textDecoration: "underline",
    padding: 0,
    marginTop: 4,
  },
  modalError: {
    color: "#ff8b7b",
    fontSize: 12,
    marginBottom: 12,
  },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    marginTop: 20,
  },
  cancelBtn: {
    background: "transparent",
    border: "1px solid var(--border, #2A1F14)",
    color: "var(--muted, #8A7A66)",
    borderRadius: 4,
    padding: "7px 14px",
    fontSize: 12,
    cursor: "pointer",
  },
  saveBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: "none",
    borderRadius: 4,
    padding: "7px 16px",
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
};
